import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "../../config";
import { db } from "../../db";
import { syncService } from "../../services/SyncService";
import { ReticleSignal, signal } from "../../reticle";

const MEDIA_RULES = {
  photo: { types: ["image/jpeg", "image/png", "image/webp", "image/heic"], maxBytes: 10 * 1024 * 1024 },
  video: { types: ["video/mp4", "video/quicktime", "video/webm"], maxBytes: 100 * 1024 * 1024 },
  pdf: { types: ["application/pdf"], maxBytes: 10 * 1024 * 1024 },
};

export function validateNoteFile(file, type) {
  const rule = MEDIA_RULES[type];
  if (!rule) return { valid: false, message: "Unknown file type" };
  if (!rule.types.includes(file.type)) {
    return { valid: false, message: `Invalid file type. Allowed: ${rule.types.join(", ")}` };
  }
  if (file.size > rule.maxBytes) {
    return { valid: false, message: `File too large. Maximum size is ${rule.maxBytes / 1024 / 1024}MB` };
  }
  return { valid: true, message: "" };
}

export function useNoteActions({ userId, token }) {
  const [uploadingNoteId, setUploadingNoteId] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);

  const createTextNote = async ({ appointmentId, content }) => {
    if (typeof appointmentId !== "string" || !appointmentId.trim()) {
      toast.error("Appointment is required");
      return false;
    }
    const trimmed = content.trim();
    if (!trimmed) return false;
    const now = new Date().toISOString();
    const note = {
      id: crypto.randomUUID(), appointment_id: appointmentId, doctor_id: userId,
      type: "text", content: trimmed, created_at: now, updated_at: now, _synced: false,
    };
    try {
      await db.appointment_notes.add(note);
      await syncService.addToQueue("create", "note", note);
      signal(ReticleSignal.NOTE_CREATED, {
        noteId: note.id, appointmentId: note.appointment_id, actorId: userId,
      });
      toast.success("Note added");
      return true;
    } catch (error) {
      console.error(error);
      toast.error("Failed to add note");
      return false;
    }
  };

  const uploadMediaToCloud = async (noteId, file) => {
    setUploadingNoteId(noteId);
    setUploadProgress(0);
    try {
      const response = await axios.post(`${API}/notes/upload-url`, {
        note_id: noteId, content_type: file.type, file_size: file.size,
      }, { headers: { Authorization: `Bearer ${token}` } });
      const { upload_url: uploadUrl, object_key: mediaKey } = response.data;
      await axios.put(uploadUrl, file, {
        headers: { "Content-Type": file.type },
        withCredentials: false,
        onUploadProgress: event => setUploadProgress(Math.round((event.loaded * 100) / event.total)),
      });
      await db.appointment_notes.update(noteId, {
        media_key: mediaKey, file_size_bytes: file.size, upload_status: "complete",
        updated_at: new Date().toISOString(), _synced: false,
      });
      const fullNote = await db.appointment_notes.get(noteId);
      const metadata = { ...fullNote };
      delete metadata.blob;
      delete metadata._synced;
      await syncService.addToQueue("update", "note", metadata);
      toast.success("Media uploaded successfully");
      return true;
    } catch (error) {
      try {
        await db.appointment_notes.update(noteId, {
          upload_status: "failed", updated_at: new Date().toISOString(),
        });
      } catch (cleanupError) {
        console.error("Failed to mark media upload as failed", cleanupError);
      }
      if (error.response?.status === 400) toast.error(error.response.data?.detail || "Upload failed");
      else if (error.response?.status === 503) toast.error("Cloud storage not configured");
      else toast.error("Upload failed. You can retry later.");
      return false;
    } finally {
      setUploadingNoteId(null);
      setUploadProgress(0);
    }
  };

  const createMediaNote = async ({ appointmentId, file, type }) => {
    if (typeof appointmentId !== "string" || !appointmentId.trim()) {
      toast.error("Appointment is required");
      return false;
    }
    const validation = validateNoteFile(file, type);
    if (!validation.valid) {
      toast.error(validation.message);
      return false;
    }
    const noteId = crypto.randomUUID();
    const now = new Date().toISOString();
    const note = {
      id: noteId, appointment_id: appointmentId, doctor_id: userId, type,
      content: file.name, upload_status: "pending", file_size_bytes: file.size,
      blob: file, created_at: now, updated_at: now, _synced: false,
    };
    try {
      await db.appointment_notes.add(note);
      const uploaded = await uploadMediaToCloud(noteId, file);
      if (uploaded) await db.appointment_notes.update(noteId, { blob: null });
      return uploaded;
    } catch (error) {
      console.error(error);
      toast.error(`Failed to add ${type}: ${error.message}`);
      return false;
    }
  };

  const retryMediaUpload = async note => {
    if (note.doctor_id !== userId) {
      toast.error("You can only retry your own notes");
      return false;
    }
    if (!note.blob) {
      toast.error("Original file not available. Please add the media again.");
      return false;
    }
    return uploadMediaToCloud(note.id, note.blob);
  };

  const deleteNote = async noteId => {
    try {
      const note = await db.appointment_notes.get(noteId);
      if (!note) {
        toast.error("Note not found");
        return false;
      }
      if (note.doctor_id !== userId) {
        toast.error("You can only delete your own notes");
        return false;
      }
      if (!window.confirm("Are you sure you want to delete this note?")) return false;
      await db.appointment_notes.delete(noteId);
      await syncService.addToQueue("delete", "note", { id: noteId });
      signal(ReticleSignal.NOTE_DELETED, {
        noteId, appointmentId: note.appointment_id, actorId: userId,
      });
      toast.success("Note deleted");
      return true;
    } catch (error) {
      console.error(error);
      toast.error("Failed to delete note");
      return false;
    }
  };

  return { createTextNote, createMediaNote, retryMediaUpload, deleteNote, uploadingNoteId, uploadProgress };
}
